"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createCustomerOrder = void 0;
const helpers_1 = require("../../helpers");
const sendEmail_1 = require("../sendEmail");
const users_1 = require("../users");
const addOrderLog_1 = require("./addOrderLog");
const uuid_1 = require("uuid");
const orders_1 = require("../../factories/orders");
/**
 * Creates a new customer order in the system.
 *
 * @param supabase - The Supabase client instance.
 * @param order_obj - The order object containing order details.
 * @returns A promise that resolves to an object indicating success or failure.
 */
const createCustomerOrder = async (supabase, order_obj) => {
    try {
        const user = await (0, users_1.createOrFindUser)({
            supabase,
            agent_id: order_obj.agent_id,
            email: order_obj.user.email,
            first_name: order_obj.user.first_name,
            last_name: order_obj.user.last_name,
            phone: order_obj.user.phone_number,
            user_type: order_obj.user.create_account ? "client" : "guest",
            country_of_residence: order_obj.user.country_of_residence,
            display_currency: order_obj.user.display_currency || "THB",
            site_id: order_obj.site_id,
        });
        const new_obj = {
            source: "DIRECT",
            method: "Web Enquiry",
            client_id: user.id,
            country_id: order_obj.cart[0].golf.where_when_who.country_id,
            mode: "AUTOMATIC",
            display_currency: order_obj?.user?.display_currency || "THB",
            status: "NEW_BOOKING",
            created_by: "8d86794f-9f63-4571-a12a-d7d4fd643b20",
            assigned_to: "8d86794f-9f63-4571-a12a-d7d4fd643b20",
            agent_id: order_obj?.agent_id,
        };
        const { data: new_order, error } = await supabase
            .from("orders")
            .insert([new_obj])
            .select()
            .single();
        if (error) {
            console.error("ERROR inserting order:", error);
            throw error;
        }
        const order_id = new_order.id;
        await (0, addOrderLog_1.addOrderLog)(supabase, {
            type: "ORDER",
            order_id: new_order.id,
            detail: {
                type: "Order Created",
                status: "NEW_BOOKING",
                title: new_order.reference,
                message: ``,
            },
        });
        const new_variant = {
            id: (0, uuid_1.v4)(),
            order_id: order_id,
            name: "Default Variant",
            created_by: "8d86794f-9f63-4571-a12a-d7d4fd643b20",
        };
        const { data: variant_data, error: variant_error } = await supabase
            .from("order_variants")
            .insert([new_variant])
            .select()
            .single();
        if (variant_error) {
            console.error("ERROR inserting order variant:", variant_error);
            throw variant_error;
        }
        const errors = [];
        const { error: update_error } = await supabase
            .from("orders")
            .update({ default_variant_id: new_variant.id })
            .eq("id", order_id);
        if (update_error) {
            console.error("ERROR updating order with variant ID:", update_error);
            errors.push(update_error);
        }
        // for each item in order_obj.cart
        for (let i = 0; i < order_obj.cart.length; i++) {
            const item = order_obj.cart[i];
            const new_golf_line = {
                order_id: order_id,
                order_variant_id: new_variant.id,
                type: "GOLF",
            };
            const { data: golf_data, error: golf_error } = await supabase
                .from("order_lines")
                .insert([new_golf_line])
                .select()
                .single();
            if (golf_error) {
                console.error("ERROR inserting golf order line:", golf_error);
                errors.push(golf_error);
                continue; // Skip to next item if golf line fails
            }
            const new_golf_line_version = {
                id: (0, uuid_1.v4)(),
                order_line_id: golf_data.id,
                config: item.golf,
                status: "PENDING",
                supplier_id: item.golf?.supplier?.id,
                start_date: item.golf?.where_when_who?.date,
                end_date: item.golf?.where_when_who?.date,
                region_id: item.golf?.where_when_who?.region_id,
                created_by: "8d86794f-9f63-4571-a12a-d7d4fd643b20",
            };
            const { data: golf_version_data, error: golf_version_error } = await supabase
                .from("order_line_versions")
                .insert([new_golf_line_version])
                .select()
                .single();
            if (golf_version_error) {
                console.error("ERROR inserting golf line version:", golf_version_error);
                errors.push(golf_version_error);
            }
            //TODO: Check transfer line is required
            if (item.transfer?.vehicle?.vehicle) {
                const new_transfer_line = {
                    order_id: order_id,
                    order_variant_id: new_variant.id,
                    type: "TRANSFER",
                };
                const { data: transfer_data, error: transfer_error } = await supabase
                    .from("order_lines")
                    .insert([new_transfer_line])
                    .select()
                    .single();
                if (transfer_error) {
                    console.error("ERROR inserting transfer order line:", transfer_error);
                    errors.push(transfer_error);
                }
                else {
                    const transfer_item_config = item.transfer;
                    transfer_item_config.where_when_who.pickup_name = `${order_obj.user.first_name} ${order_obj.user.last_name}`;
                    const new_transfer_line_version = {
                        id: (0, uuid_1.v4)(),
                        order_line_id: transfer_data.id,
                        config: transfer_item_config,
                        status: "PENDING",
                        supplier_id: item.transfer?.supplier?.id,
                        start_date: item.transfer?.where_when_who?.date,
                        end_date: item.transfer?.where_when_who?.date,
                        region_id: item.transfer?.where_when_who?.region_id,
                        linked_line_id: golf_data.id,
                        created_by: "8d86794f-9f63-4571-a12a-d7d4fd643b20",
                    };
                    const { data: transfer_version_data, error: transfer_version_error } = await supabase
                        .from("order_line_versions")
                        .insert([new_transfer_line_version])
                        .select()
                        .single();
                    if (transfer_version_error) {
                        console.error("ERROR inserting transfer line version:", transfer_version_error);
                        errors.push(transfer_version_error);
                    }
                }
            }
            if (item.note) {
                const note = {
                    id: (0, uuid_1.v4)(),
                    created_by: user.id,
                    order_item_id: golf_data.id,
                    order_id: order_id,
                    note: `Special Request - ${item.note}`,
                };
                const { data: note_data, error: note_error } = await supabase
                    .from("order_notes")
                    .insert([note]);
                if (note_error) {
                    console.error("ERROR inserting note:", note_error);
                    errors.push(note_error);
                }
            }
        }
        if (errors.length > 0) {
            console.error("ERRORS encountered during processing:", errors);
            return {
                success: false,
                data: errors,
            };
        }
        // we have gotten to this point...send the confirmation email to the customer.
        const email_obj = {
            language_code: "en",
            template_id: helpers_1.EMAIL_ORDER_ACKNOWLEDGEMENT,
            order_id: order_id,
        };
        if (order_obj.user.create_account) {
            // if they are creating an account send them an email with their temp password with a link to login and change it.
        }
        await (0, sendEmail_1.sendEmail)(email_obj);
        const support_rate_enquiry_email_obj = {
            ...email_obj,
            template_id: helpers_1.NEW_GOLF_BOOKING_SUPPORT_NOTIFICATION,
        };
        await (0, sendEmail_1.sendEmail)(support_rate_enquiry_email_obj);
        // Finally create initial order snapshot
        await (0, orders_1.createOrderSnapshot)(supabase, order_id, helpers_1.OrderSnapshotStates.ORDER_RECEIVED, helpers_1.SnapshotStatus.PUBLISHED, helpers_1.OrderStatus.NEW_BOOKNG);
        return {
            success: true,
            data: new_order,
        };
    }
    catch (error) {
        console.error("=== FATAL ERROR in createCustomerOrder ===");
        console.error("Error details:", error);
        return {
            success: false,
            data: [error],
        };
    }
};
exports.createCustomerOrder = createCustomerOrder;
